/* eslint-disable import/no-named-as-default-member */
import axios from 'axios';
import axiosRetry, { isNetworkError, exponentialDelay } from 'axios-retry';

import { getCookie, setCookie, clearAllCookies } from '@helpers/cookie';
import { isEmpty } from '@helpers/utils/utils';
import { NETWORK_ERROR } from '@helpers/constants/State';
import { TOKEN } from './constants';
import { urlUtils } from '@helpers/urlUtils.js';
import oktaAuth from '@core/Okta/oktaAuth';

let REFRESH_TOKEN_PROMISE = null;

const shouldRetry = (error) => {
  return (
    isNetworkError(error) ||
    error.code === 'ECONNABORTED' ||
    (error.response && error.response.status === 429) ||
    (error.response && isEmpty(error.response.data))
  );
};

const retryConfig = {
  retries: 2,
  retryDelay: exponentialDelay,
  retryCondition: shouldRetry,
  shouldResetTimeout: true,
};

const request = axios.create({
  timeout: 120000,
  headers: {
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
  },
  // withCredentials: true,
});

axiosRetry(request, retryConfig);

const renewAccessToken = async () => {
  const tokens = await oktaAuth.token.renewTokens();
  const newAccessToken = tokens?.accessToken?.accessToken || '';

  if (!isEmpty(newAccessToken)) {
    oktaAuth.tokenManager.setTokens(tokens);
    oktaAuth.authStateManager.updateAuthState();
    // setCookie(TOKEN.SP_ACCESS_TOKEN, newAccessToken, 86400000);
    setCookie(
      TOKEN.SP_ACCESS_TOKEN,
      newAccessToken,
      tokens.accessToken.expiresAt,
    );
  }

  return newAccessToken;
};

const redirectToLogin = () => {
  console.error('Unauthorized: Clear all storages');
  sessionStorage.clear();
  clearAllCookies();
  window.location.replace('/sales-portal/login');
};

const handleError = (error) => {
  if (error.message === 'Network Error') {
    const errorMessage = {
      status: NETWORK_ERROR,
      data: {
        message: error.message,
      },
    };
    return errorMessage;
  }

  throw error;
};

request.interceptors.request.use(
  async (config) => {
    const SPAccessToken = getCookie(TOKEN.SP_ACCESS_TOKEN);

    if (!config?.skipAuth) {
      config.headers = {
        ...config.headers,
        ...(SPAccessToken
          ? {
              Authorization: `Bearer ${SPAccessToken}`,
            }
          : {}),
      };
    }

    // eslint-disable-next-line consistent-return
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

request.interceptors.response.use(
  (response) => {
    const statusCode = response?.status || -1;
    const isValidStatus = statusCode >= 200 && statusCode < 300;
    if (isValidStatus) {
      return response.data;
    }
  },
  async (error) => {
    const originalRequest = error?.config;

    if (
      error?.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest?.skipAuth
    ) {
      originalRequest._retry = true;

      try {
        if (!REFRESH_TOKEN_PROMISE) {
          REFRESH_TOKEN_PROMISE = renewAccessToken().finally(() => {
            REFRESH_TOKEN_PROMISE = null;
          });
        }

        const newAccessToken = await REFRESH_TOKEN_PROMISE;

        if (isEmpty(newAccessToken)) {
          redirectToLogin();
          return Promise.reject(error);
        }

        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAccessToken}`,
        };

        return request(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const GET_REQUEST = (root, data, extHeaders = null) => {
  return request(urlUtils.buildFullPath(root, data), {
    method: 'GET',
    ...extHeaders,
  });
};

export const POST_FORM_DATA_REQUEST = (
  root,
  data,
  queryParams = null,
  extHeaders = null,
) => {
  const formData = new FormData();
  if (data) {
    for (const dataKey in data) {
      formData.set(dataKey, data[dataKey]);
    }
  }
  return request(urlUtils.buildFullPath(root, queryParams), {
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data', ...extHeaders },
    data: formData,
  });
};
export const POST_REQUEST = (root, data, extConfigs = null) => {
  return request(root, {
    method: 'POST',
    data: { ...data },
    ...extConfigs,
  });
};

export const POST_ENCODE_URI_COMPONENT_REQUEST = (
  root,
  data,
  queryParams = null,
  extHeaders = null,
) => {
  const bodyMap = new Map();
  const body = [];
  if (data) {
    for (const dataKey in data) {
      bodyMap.set(dataKey, data[dataKey]);
    }
    bodyMap.forEach((value, key) => {
      body.push(`${key}=${encodeURIComponent(value)}`);
    });
  }
  return request(urlUtils.buildFullPath(root, queryParams), {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    data: body.join('&'),
  });
};

export default request;
